import React, {
  useRef,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Slider,
  Typography,
} from "@mui/material";
import {
  PlayArrow,
  Pause,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";

import { VideoPlayerContext } from "../../context/VideoPlayerContext";
import { ThemeContext } from "../../context/ThemeContext";
import { isIpad } from "../../utils/isIPad";

export const CustomVideoPlayer = ({
  src,
  onError,
  activeTool,
  setActiveTool,
  isVisibleSeekbar,
  clearRequested,
  resetClearRequest,
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const playerRef = useRef(null); // Reference for the player container
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [ctx, setCtx] = useState(null);
  const { isLoading, setIsLoading } = useContext(VideoPlayerContext);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isCustomFullScreen, setIsCustomFullScreen] = useState(false); // Custom full-screen state for iPads

  const { darkMode } = useContext(ThemeContext);
  const intervalRef = useRef(null); // Reference to store the interval ID
  const videoContainerRef = useRef(null);

  const resizeCanvas = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      canvas.width = video.clientWidth;
      canvas.height = video.clientHeight;

      const context = canvas.getContext("2d");
      context.lineWidth = 8;
      context.strokeStyle = "yellow";
      context.lineJoin = "round";
      context.lineCap = "round";
      setCtx(context);
    }
  };

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === videoRef.current) {
          resizeCanvas();
        }
      }
    });

    // Start observing the element
    resizeObserver.observe(videoRef.current);
  }, []);

  const updateSeek = (seek) => {
    if (videoRef.current) {
      const currentTime = seek ?? videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      if (duration) {
        const newProgress = (currentTime / duration) * 100;
        setProgress(newProgress);
      }
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        clearInterval(intervalRef.current); // Clear the interval when pausing
        videoRef.current.pause();
      } else {
        intervalRef.current = setInterval(() => {
          if (videoRef.current) {
            updateSeek();
          }
        }, 50); // Update progress every second

        if (progress === 100) {
          updateSeek(0);
          videoRef.current.currentTime = 0;
          setTimeout(() => {
            videoRef.current.play();
          }, 100);
        } else {
          videoRef.current.play();
        }
      }
      setIsPlaying(!isPlaying);
    }
  };

  // const handleSeek = (newValue) => {
  //   setProgress(newValue);
  //   if (videoRef.current) {
  //     videoRef.current.currentTime = videoRef.current.duration
  //       ? (newValue / 100) * videoRef.current.duration
  //       : 0;
  //   }
  // };

  const handleSeek = (event, newValue) => {
    // event.preventDefault();
    // event.stopPropagation();
    if (videoRef.current) {
      videoRef.current.currentTime = videoRef.current.duration
        ? (newValue / 100) * videoRef.current.duration
        : 0;
      setProgress(videoRef.current.duration ? newValue : 0);
    }
  };

  const handleError = () => {
    setHasError(true);
    setIsLoading(false);
    setIsPlaying(false);
    setProgress(0);
    if (onError) onError();
  };

  const handleMouseDown = (e) => {
    if (activeTool !== "draw" || !ctx) return;

    // Calculate the scale factor to adjust mouse position based on canvas size vs video size
    const scaleX = canvasRef.current.width / videoRef.current.clientWidth;
    const scaleY = canvasRef.current.height / videoRef.current.clientHeight;

    setIsDrawing(true);
    ctx.beginPath();

    // Adjust mouse coordinates for scaling
    const offsetX = e.nativeEvent.offsetX / scaleX;
    const offsetY = e.nativeEvent.offsetY / scaleY;

    ctx.moveTo(offsetX, offsetY);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !ctx) return;

    // Calculate the scale factor to adjust mouse position based on canvas size vs video size
    const scaleX = canvasRef.current.width / videoRef.current.clientWidth;
    const scaleY = canvasRef.current.height / videoRef.current.clientHeight;

    // Adjust mouse coordinates for scaling
    const offsetX = e.nativeEvent.offsetX / scaleX;
    const offsetY = e.nativeEvent.offsetY / scaleY;

    ctx.lineTo(offsetX, offsetY);
    ctx.stroke();
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  const handleTouchStart = (e) => {
    // e.preventDefault();
    if (activeTool !== "draw" || !ctx) return;

    // Calculate the scale factor to adjust touch position based on canvas size vs video size
    const scaleX = canvasRef.current.width / videoRef.current.clientWidth;
    const scaleY = canvasRef.current.height / videoRef.current.clientHeight;

    setIsDrawing(true);
    ctx.beginPath();

    // Adjust touch coordinates for scaling
    const touch = e.touches[0];
    const rect = canvasRef.current.getBoundingClientRect();

    const offsetX = (touch.clientX - rect.left) / scaleX;
    const offsetY = (touch.clientY - rect.top) / scaleY;

    ctx.moveTo(offsetX, offsetY);
  };

  const handleTouchMove = (e) => {
    // e.preventDefault();
    if (!isDrawing || !ctx) return;

    // Calculate the scale factor to adjust touch position based on canvas size vs video size
    const scaleX = canvasRef.current.width / videoRef.current.clientWidth;
    const scaleY = canvasRef.current.height / videoRef.current.clientHeight;

    // Adjust touch coordinates for scaling
    const touch = e.touches[0];
    const rect = canvasRef.current.getBoundingClientRect();
    const offsetX = (touch.clientX - rect.left) / scaleX;
    const offsetY = (touch.clientY - rect.top) / scaleY;

    ctx.lineTo(offsetX, offsetY);
    ctx.stroke();
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = useCallback(() => {
    if (ctx) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }

    resetClearRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx]);

  useEffect(() => {
    if (clearRequested) {
      clearCanvas();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearRequested]);

  useEffect(() => {
    setIsLoading(true);
    handleSeek(null, 0);
    setIsPlaying(false);
  }, [src, setIsLoading]);

  // Full-screen functionality
  const toggleFullScreen = () => {
    if (isIpad()) {
      // Custom full-screen mode for iPads
      setIsCustomFullScreen(!isCustomFullScreen);
    } else {
      // Native full-screen mode for other devices
      if (playerRef.current) {
        if (!isFullScreen) {
          if (playerRef.current.requestFullscreen) {
            playerRef.current.requestFullscreen();
          } else if (playerRef.current.webkitRequestFullscreen) {
            playerRef.current.webkitRequestFullscreen();
          } else if (playerRef.current.mozRequestFullScreen) {
            playerRef.current.mozRequestFullScreen();
          } else if (playerRef.current.msRequestFullscreen) {
            playerRef.current.msRequestFullscreen();
          }
          setIsFullScreen(true);
        } else {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen();
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen();
          }
          setIsFullScreen(false);
        }
      }
    }
  };

  // Listen for fullscreen changes (when the escape key is pressed or full-screen mode changes)
  useEffect(() => {
    const onFullScreenChange = () => {
      if (
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      ) {
        setIsFullScreen(true);
      } else {
        setIsFullScreen(false);
      }
    };

    document.addEventListener("fullscreenchange", onFullScreenChange);
    document.addEventListener("webkitfullscreenchange", onFullScreenChange);
    document.addEventListener("mozfullscreenchange", onFullScreenChange);
    document.addEventListener("MSFullscreenChange", onFullScreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", onFullScreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        onFullScreenChange
      );
      document.removeEventListener("mozfullscreenchange", onFullScreenChange);
      document.removeEventListener("MSFullscreenChange", onFullScreenChange);
    };
  }, []);

  // Dynamically adjust video and canvas size based on full-screen state
  useEffect(() => {
    const video = videoContainerRef.current;
    if (video) {
      if (isFullScreen) {
        video.style.width = "100vw";
        video.style.height = isIpad() ? "90vh" : "95vh";
      } else {
        video.style.width = "100%";
        video.style.height = "calc(97% - 65px)";
      }
    }

    // Adjust canvas size when video is in full-screen mode
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      if (isFullScreen) {
        canvas.style.width = "100vw";
        canvas.style.height = "95vh";
      } else {
        canvas.style.width = "100%";
        canvas.style.height = "100%";
      }
    }
  }, [isFullScreen]);

  return (
    <Box
      ref={playerRef} // Reference to the player container
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        opacity: isLoading ? 0 : 1,
        height: isCustomFullScreen ? "90vh" : "100%",
        width: isCustomFullScreen ? "100vw" : "100%",
        position: isCustomFullScreen ? "fixed" : "relative",
        top: isCustomFullScreen ? 0 : "auto",
        left: isCustomFullScreen ? 0 : "auto",
        background: isCustomFullScreen ? "black" : "transparent",
        zIndex: isCustomFullScreen ? 9999 : "auto",
      }}
    >
      <Box
        position="relative"
        display="flex"
        flexDirection="row"
        alignItems="flex-start"
        justifyContent="center"
        ref={videoContainerRef}
        sx={{
          width: "100%",
          height: isCustomFullScreen ? "100vh" : "100%",
        }}
      >
        {!hasError ? (
          <>
            <video
              ref={videoRef}
              src={src}
              onContextMenu={(e) => e.preventDefault()}
              onLoadedMetadata={resizeCanvas}
              onLoadedData={() => setIsLoading(false)}
              onWaiting={() => setIsLoading(true)}
              onPlaying={() => setIsLoading(false)}
              onEnded={() => {
                setIsPlaying(false);
                setProgress(100);
                clearInterval(intervalRef.current); // Clear the interval when video ends
              }}
              onError={handleError}
              style={{
                background: "black",
                position: "relative",
                objectFit: "fill",
                width: "100%",
                height: "100%",
              }}
            />
            <canvas
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                pointerEvents: activeTool === "draw" ? "auto" : "none",
              }}
            />
          </>
        ) : (
          <Typography color="red">Error loading video</Typography>
        )}

        {isLoading && !hasError && (
          <CircularProgress
            size={50}
            sx={{ position: "absolute", color: "white" }}
          />
        )}
      </Box>

      {isVisibleSeekbar && !hasError && (
        <Box
          width={isFullScreen || isCustomFullScreen ? "95%" : "100%"}
          display="flex"
          alignItems="center"
          mt={1}
          sx={{
            position: isCustomFullScreen ? "absolute" : "relative",
            bottom: isCustomFullScreen ? "20px" : "auto",
          }}
        >
          <IconButton onClick={togglePlayPause}>
            {isPlaying ? (
              <Pause
                sx={{
                  color:
                    isFullScreen || isCustomFullScreen
                      ? "white "
                      : darkMode
                      ? "white"
                      : "black",
                }}
              />
            ) : (
              <PlayArrow
                sx={{
                  color:
                    isFullScreen || isCustomFullScreen
                      ? "white "
                      : darkMode
                      ? "white"
                      : "black",
                }}
              />
            )}
          </IconButton>
          <Slider
            value={progress}
            onChange={handleSeek}
            sx={{
              flexGrow: 1,
              mx: 1,
              "& .MuiSlider-thumb": {
                width: 26,
                height: 26,
              },
            }}
            min={0}
            max={100}
            step={0.05}
          />
          <IconButton onClick={toggleFullScreen}>
            {isFullScreen || isCustomFullScreen ? (
              <FullscreenExit sx={{ color: "white" }} />
            ) : (
              <Fullscreen sx={{ color: darkMode ? "white" : "black" }} />
            )}
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
